import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useFetch from '../../../../components/useFetch';
import './UserProfileEdit.scss';
import '../../UserActivityPage.scss';

import {
    ApiTransferIcon,
    CameraIcon,
    DeleteIcon,
    EyeIcon,
    EyeOffIcon,
    GearIcon,
    GlobeAltIcon,
    LockIcon,
    TrustIcon,
    UserIcon,
} from '../../../Common/SvgIcons/WorkflowIcons';
import PathBreadcrumb from '../../../../components/PathBreadcrumb/PathBreadcrumb';
import TitleHeader from '../../../project/header/TitleHeader';
import getDataFromApi from '../../../../components/getDataFromApi';
import UserActivityPage from '../../UserActivityPage';

const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

const resolveUserAvatar = (user, { allowLocal = false } = {}) => {
    const fromUser =
        user?.profile_image ||
        user?.user_image ||
        user?.image ||
        '';
    if (fromUser) return fromUser;
    if (!allowLocal) return '';
    const local = localStorage.getItem('navImage') || localStorage.getItem('profile_image') || '';
    if (!local || local === 'null' || local === 'undefined') return '';
    return local;
};

export const UserProfileEdit = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const role = localStorage.getItem("role");

    const passedUser = location.state?.user;
    const viewFrom = location.state?.viewFrom;
    const userId = location.state?.id || passedUser?.id || localStorage.getItem('user_id');
    const { data: fetchedUser } = useFetch(userId ? `users/${userId}` : null, "get");
    const activeUser =
        fetchedUser && String(fetchedUser?.id) === String(userId)
            ? fetchedUser
            : passedUser || null;
    const currentUserId = localStorage.getItem('user_id');
    const isEditingSelf = String(userId) === String(currentUserId);
    const isUserManagementView = viewFrom === 'user_details' && !isEditingSelf;
    const canManageApiKeys =
        isEditingSelf || role === 'system_admin' || role === 'tenant_admin';
    const goToOwnProfile = () =>
        navigate('/user-profile', {
            state: {
                user: activeUser,
                id: userId,
                viewFrom: 'profile_dashboard',
            },
        });

    const targetRole = useMemo(() => {
        const roles = activeUser?.roles;
        const raw = Array.isArray(roles)
            ? roles[0]
            : (roles ?? activeUser?.role ?? '');
        return String(raw).toLowerCase().replace(/\s+/g, '_');
    }, [activeUser]);

    const canDeleteUser = useMemo(() => {
        if (!userId || isEditingSelf) return false;
        if (!targetRole) return false;

        if (role === 'system_admin') {
            // System admin can delete tenant admins and users, but not themselves.
            return targetRole !== 'system_admin';
        }

        if (role === 'tenant_admin') {
            // Tenant admin can delete users, but not themselves or system/tenant admins.
            return targetRole !== 'system_admin' && targetRole !== 'tenant_admin';
        }

        // Regular users cannot delete anyone (including tenant admins).
        return false;
    }, [userId, isEditingSelf, role, targetRole]);

    const initials = useMemo(() => {
        const name = activeUser?.name || 'User';
        return name
            .split(' ')
            .map(word => word[0])
            .join('')
            .slice(0, 1)
            .toUpperCase();
    }, [activeUser]);

    const [activeTab, setActiveTab] = useState(
        location.state?.showApiKeys ? 'api-keys' : 'profile'
    );
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [submitSection, setSubmitSection] = useState(null);
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        next: false,
        confirm: false,
    });

    const togglePasswordVisibility = (field) => {
        setShowPasswords((prev) => ({
            ...prev,
            [field]: !prev[field],
        }));
    };

    const renderPasswordToggle = (field) => {
        const visible = Boolean(showPasswords[field]);
        return (
            <button
                type="button"
                className="upe-password-wrap__toggle"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => togglePasswordVisibility(field)}
                aria-label={visible ? 'Hide password' : 'Show password'}
                aria-pressed={visible}
            >
                {visible
                    ? <EyeOffIcon className="wf-icon-small" />
                    : <EyeIcon className="wf-icon-small" />}
            </button>
        );
    };

    const [submitLoading, setSubmitLoading] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [submitSuccess, setSubmitSuccess] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});


    const validateEmail = (email) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const validateProfileForm = () => {
        const errors = {};

        if (!profileData.displayName?.trim()) {
            errors.displayName = 'Display name is required.';
        }

        if (!profileData.email?.trim()) {
            errors.email = 'Email is required.';
        } else if (!validateEmail(profileData.email)) {
            errors.email = 'Please enter a valid email address.';
        }

        if (profileData.bio && profileData.bio.length > 300) {
            errors.bio = 'Bio must be less than 300 characters.';
        }

        return errors;
    };

    const validatePasswordForm = () => {
        const errors = {};

        const requireCurrentPassword =
            role === 'developer';

        if (requireCurrentPassword && !passwordData.currentPassword) {
            errors.currentPassword = 'Current password is required.';
        }

        if (!passwordData.newPassword) {
            errors.newPassword = 'New password is required.';
        } else if (passwordData.newPassword.length < 8) {
            errors.newPassword = 'New password must be at least 8 characters.';
        }

        if (!passwordData.confirmPassword) {
            errors.confirmPassword = 'Please confirm your new password.';
        } else if (passwordData.newPassword !== passwordData.confirmPassword) {
            errors.confirmPassword = 'Passwords do not match.';
        }

        return errors;
    };



    const [profileData, setProfileData] = useState({
        displayName: passedUser?.name || 'Tesr',
        email: passedUser?.email || 'tesr@tesr.com',
        bio: passedUser?.bio || '',
        profile_image: resolveUserAvatar(passedUser, { allowLocal: isEditingSelf }),
    });

    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });

    const avatarInputRef = useRef(null);
    const [avatarUploading, setAvatarUploading] = useState(false);

    const openAvatarPicker = () => {
        avatarInputRef.current?.click();
    };

    const handleAvatarFileChange = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;

        if (!file.type?.startsWith('image/')) {
            setSubmitSection('profile');
            setSubmitError('Please select a valid image file.');
            setSubmitSuccess(null);
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            setSubmitSection('profile');
            setSubmitError('Image must be 5MB or smaller.');
            setSubmitSuccess(null);
            return;
        }

        try {
            setAvatarUploading(true);
            const base64 = await fileToBase64(file);
            setProfileData((prev) => ({
                ...prev,
                profile_image: base64,
            }));
            setSubmitSection('profile');
            setSubmitError(null);
            setSubmitSuccess(null);
        } catch {
            setSubmitSection('profile');
            setSubmitError('Could not read that image. Please try another file.');
            setSubmitSuccess(null);
        } finally {
            setAvatarUploading(false);
        }
    };

    const handleProfileChange = (e) => {
        const { name, value } = e.target;

        setProfileData((prev) => ({
            ...prev,
            [name]: value,
        }));

        setValidationErrors((prev) => ({
            ...prev,
            [name]: '',
        }));

        setSubmitError(null);
        setSubmitSuccess(null);
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;

        setPasswordData((prev) => ({
            ...prev,
            [name]: value,
        }));

        setValidationErrors((prev) => ({
            ...prev,
            [name]: '',
        }));

        setSubmitError(null);
        setSubmitSuccess(null);
    };

    const originalProfileData = useMemo(() => ({
        displayName: activeUser?.name || '',
        email: activeUser?.email || '',
        bio: activeUser?.bio || '',
        profile_image: resolveUserAvatar(activeUser, { allowLocal: isEditingSelf }),
    }), [activeUser, isEditingSelf]);

    useEffect(() => {
        setProfileData({
            displayName: activeUser?.name || '',
            email: activeUser?.email || '',
            bio: activeUser?.bio || '',
            profile_image: resolveUserAvatar(activeUser, { allowLocal: isEditingSelf }),
        });
        setPasswordData({
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
        });
        setValidationErrors({});
        setSubmitError(null);
        setSubmitSuccess(null);
        setSubmitSection(null);
        setShowDeleteModal(false);
        setActiveTab(location.state?.showApiKeys ? 'api-keys' : 'profile');
    }, [activeUser?.id, activeUser?.name, activeUser?.email, activeUser?.bio, activeUser?.profile_image, activeUser?.user_image, activeUser?.image, isEditingSelf, location.state?.showApiKeys]);

    const handleProfileSubmit = (e) => {
        e.preventDefault();
        setSubmitSection('profile');

        if (!userId) {
            setSubmitError('User id not found.');
            setSubmitSuccess(null);
            return;
        }

        // ✅ Check if anything changed
        const isUnchanged =
            profileData.displayName === originalProfileData.displayName &&
            profileData.email === originalProfileData.email &&
            profileData.bio === originalProfileData.bio &&
            profileData.profile_image === originalProfileData.profile_image;

        if (isUnchanged) {
            setSubmitError('No changes detected.');
            setSubmitSuccess(null);
            return;
        }

        const errors = validateProfileForm();
        setValidationErrors(errors);
        setSubmitError(null);
        setSubmitSuccess(null);

        if (Object.keys(errors).length > 0) return;

        setSubmitLoading(true);

        const payload = {
            name: profileData.displayName,
            email: profileData.email,
            bio: profileData.bio,
            profile_image: profileData.profile_image || '',
            user_image: profileData.profile_image || '',
        };

        const persistSelfAvatar = async () => {
            if (!isEditingSelf) return;
            await getDataFromApi(
                `${window.ENV.API_URL}/home/users`,
                'PUT',
                {
                    name: profileData.displayName,
                    email: profileData.email,
                    role: localStorage.getItem('role') || '',
                    profile_image: profileData.profile_image || '',
                }
            );
        };

        getDataFromApi(
            `${window.ENV.API_URL}/users/${userId}`,
            'PATCH',
            payload
        )
            .then(async (res) => {
                try {
                    await persistSelfAvatar();
                } catch {
                    // Admin PATCH may already persist; home/users is best-effort for nav avatar.
                }
                setSubmitLoading(false);
                setSubmitSuccess(res?.message || 'Profile updated successfully.');
                setSubmitError(null);
                setValidationErrors({});
                if (isEditingSelf && profileData.profile_image) {
                    localStorage.setItem('navImage', profileData.profile_image);
                    localStorage.setItem('profile_image', profileData.profile_image);
                }
                if (isEditingSelf && profileData.displayName) {
                    localStorage.setItem('name', profileData.displayName);
                }
            })
            .catch(async (e) => {
                // Fallback for own profile when admin PATCH rejects avatar/bio payload.
                if (isEditingSelf) {
                    try {
                        await persistSelfAvatar();
                        setSubmitLoading(false);
                        setSubmitSuccess('Profile updated successfully.');
                        setSubmitError(null);
                        setValidationErrors({});
                        if (profileData.profile_image) {
                            localStorage.setItem('navImage', profileData.profile_image);
                            localStorage.setItem('profile_image', profileData.profile_image);
                        }
                        if (profileData.displayName) {
                            localStorage.setItem('name', profileData.displayName);
                        }
                        return;
                    } catch {
                        // fall through to original error
                    }
                }
                setSubmitLoading(false);
                setSubmitSuccess(null);
                setSubmitError(
                    e?.response?.data?.message ||
                    e?.message ||
                    'Failed to update profile. Please try again.'
                );
            });
    };


    const handlePasswordSubmit = (e) => {
        e.preventDefault();
        setSubmitSection('security');

        if (!userId) {
            setSubmitError('User id not found.');
            setSubmitSuccess(null);
            return;
        }

        const errors = validatePasswordForm();
        setValidationErrors(errors);
        setSubmitError(null);
        setSubmitSuccess(null);

        if (Object.keys(errors).length > 0) return;

        setSubmitLoading(true);

        const payload = {
            current_password: passwordData.currentPassword,
            new_password: passwordData.newPassword,
            confirm_password: passwordData.confirmPassword,
        };

        getDataFromApi(
            `${window.ENV.API_URL}/users/${userId}/password`,
            'post',
            payload
        )
            .then((res) => {
                setSubmitLoading(false);
                setSubmitSuccess(res?.message || 'Password updated successfully.');
                setSubmitError(null);
                setValidationErrors({});
                setPasswordData({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: '',
                });
            })
            .catch((e) => {
                setSubmitLoading(false);
                setSubmitSuccess(null);
                setSubmitError(
                    e?.response?.data?.message ||
                    e?.message ||
                    'Failed to change password. Please try again.'
                );
            });
    };

    const handleDeleteAccount = () => {
        if (!canDeleteUser || !userId) {
            setSubmitError('You are not allowed to delete this user.');
            setSubmitSuccess(null);
            setShowDeleteModal(false);
            return;
        }

        setSubmitLoading(true);
        setSubmitError(null);
        setSubmitSuccess(null);

        getDataFromApi(
            `${window.ENV.API_URL}/users/${userId}`,
            'delete'
        )
            .then((res) => {
                setSubmitLoading(false);
                setSubmitSuccess(res?.message || 'Account deleted successfully.');
                navigate('/admin/users');
            })
            .catch((e) => {
                setSubmitLoading(false);
                setSubmitSuccess(null);
                setSubmitError(
                    e?.response?.data?.message ||
                    e?.message ||
                    'Failed to delete account. Please try again.'
                );
            });
    };

    return (
        <>

            <div className="mainmenu projects-content de-width">
                <PathBreadcrumb
                    onBack={() => {
                      if (isUserManagementView) {
                        navigate(-1);
                        return;
                      }
                      goToOwnProfile();
                    }}
                    items={
                      isUserManagementView
                        ? [
                            { label: 'Admin', onClick: () => navigate('/admin') },
                            { label: 'Users', onClick: () => navigate(-1) },
                            { label: 'Settings' },
                          ]
                        : [
                            { label: 'Profile', onClick: goToOwnProfile },
                            { label: 'Settings' },
                          ]
                    }
                />

                <div className="d-a justify-content-between gap-3">
                    <TitleHeader
                        Icon={GearIcon}
                        title1={isEditingSelf ? 'Account' : 'User'}
                        title2={' Settings'}
                        subtext={
                            isEditingSelf
                                ? 'Configure your profile, security, and API keys'
                                : 'Configure profile and security preferences'
                        }
                    />
                </div>


                <div className="d-flex gap-4 mt-4 admin-content admin-content-user">
                    <div className="admin-content-card2 upe-sidebar-col">
                        <div className="upe-sidebar">
                            <button
                                className={`upe-sidebar-btn ${activeTab === 'profile' ? 'active' : ''}`}
                                onClick={() => setActiveTab('profile')}
                            >
                                <UserIcon className="wf-icon-small" />
                                <span>Edit Profile</span>
                            </button>

                            <button
                                className={`upe-sidebar-btn ${activeTab === 'security' ? 'active' : ''}`}
                                onClick={() => setActiveTab('security')}
                            >
                                <TrustIcon className="wf-icon-small" />
                                <span>Security & Password</span>
                            </button>

                            {canManageApiKeys && (
                                <button
                                    className={`upe-sidebar-btn ${activeTab === 'api-keys' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('api-keys')}
                                >
                                    <ApiTransferIcon className="wf-icon-small" />
                                    <span>API Key Management</span>
                                </button>
                            )}
                            {canDeleteUser &&
                                <button
                                    className="upe-sidebar-btn danger"
                                    onClick={() => setShowDeleteModal(true)}
                                >
                                    <DeleteIcon className="wf-icon-small" />
                                    <span>Delete User</span>
                                </button>}
                        </div>
                    </div>

                    <div className="container-card-right flex-fill">
                        {canManageApiKeys && (
                            <div
                                className={`upe-card upe-api-keys-panel ${
                                    activeTab === 'api-keys' ? '' : 'd-none'
                                }`}
                            >
                                <UserActivityPage
                                    embedded
                                    userId={userId}
                                    user={activeUser}
                                />
                            </div>
                        )}
                        {activeTab === 'profile' && (
                            <form className="upe-card" onSubmit={handleProfileSubmit}>
                                <div className="upe-card-head">
                                    <h4>Public Profile</h4>
                                </div>

                                <div className="upe-profile-layout">
                                    <div className="upe-avatar-section">
                                        <div className="upe-avatar-box">
                                            {profileData.profile_image ? (
                                                <img
                                                    src={profileData.profile_image}
                                                    alt=""
                                                    className="upe-avatar-img"
                                                />
                                            ) : (
                                                profileData.displayName?.charAt(0)?.toUpperCase() || initials
                                            )}
                                        </div>

                                        <button
                                            type="button"
                                            className="upe-avatar-camera"
                                            onClick={openAvatarPicker}
                                            disabled={avatarUploading || submitLoading}
                                            aria-label="Change avatar"
                                        >
                                            <CameraIcon className="wf-icon-small" />
                                        </button>

                                        <button
                                            type="button"
                                            className="upe-change-avatar"
                                            onClick={openAvatarPicker}
                                            disabled={avatarUploading || submitLoading}
                                        >
                                            {avatarUploading ? 'Loading…' : 'Change Avatar'}
                                        </button>

                                        <input
                                            ref={avatarInputRef}
                                            type="file"
                                            accept="image/*"
                                            className="d-none"
                                            onChange={handleAvatarFileChange}
                                        />
                                    </div>

                                    <div className="upe-form-area">
                                        <div className="upe-form-grid">
                                            <div className="upe-field">
                                                <label>Display Name</label>
                                                <input
                                                    type="text"
                                                    name="displayName"
                                                    value={profileData.displayName}
                                                    onChange={handleProfileChange}
                                                    placeholder="Enter display name"
                                                />
                                                {validationErrors.displayName && (
                                                    <small className="text-danger mt-1 d-block">
                                                        {validationErrors.displayName}
                                                    </small>
                                                )}
                                            </div>

                                            <div className="upe-field">
                                                <label>Email Address</label>
                                                <input
                                                    type="email"
                                                    name="email"
                                                    value={profileData.email}
                                                    // onChange={handleProfileChange}
                                                    disabled
                                                    placeholder="Enter email"
                                                />
                                                {validationErrors.email && (
                                                    <small className="text-danger mt-1 d-block">
                                                        {validationErrors.email}
                                                    </small>
                                                )}
                                                <small className="text-secondary mt-1 d-block">
                                                    * Email address cannot be changed.
                                                </small>
                                            </div>

                                            <div className="upe-field full">
                                                <label>Bio / Description</label>
                                                <textarea
                                                    name="bio"
                                                    value={profileData.bio}
                                                    onChange={handleProfileChange}
                                                    placeholder="Tell us about yourself..."
                                                    rows={5}
                                                />
                                                {validationErrors.bio && (
                                                    <small className="text-danger mt-1 d-block">
                                                        {validationErrors.bio}
                                                    </small>
                                                )}
                                            </div>
                                        </div>

                                        {submitSection === 'profile' && submitSuccess && (
                                            <div className="alert alert-success mt-3">
                                                {submitSuccess}
                                            </div>
                                        )}

                                        {submitSection === 'profile' && submitError && (
                                            <div className="alert alert-danger mt-3">
                                                {submitError}
                                            </div>
                                        )}


                                        <div className="upe-actions end">
                                            <button
                                                type="submit"
                                                className="upe-primary-btn btn-click"
                                                disabled={submitLoading || avatarUploading}
                                            >
                                                {submitLoading ? 'Updating...' : 'Update Profile'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        )}

                        {activeTab === 'security' && (
                            <form className="upe-card" onSubmit={handlePasswordSubmit}>
                                <div className="upe-card-head">
                                    <h4>Security & Password</h4>
                                </div>

                                <div className="upe-security-block">
                                    <div className="upe-section-title">
                                        <div className="upe-section-icon wf-icon-yellow rounded-img">
                                            <LockIcon className="wf-icon-small" />
                                        </div>
                                        <h5>Change Password</h5>
                                    </div>


                                    {role !== 'tenant_admin' && role !== 'system_admin' &&
                                        <div className="upe-field full">
                                            <label>Current Password</label>
                                            <div className="upe-password-wrap">
                                                <input
                                                    type={showPasswords.current ? 'text' : 'password'}
                                                    name="currentPassword"
                                                    value={passwordData.currentPassword}
                                                    onChange={handlePasswordChange}
                                                    placeholder="••••••••"
                                                    autoComplete="current-password"
                                                />
                                                {renderPasswordToggle('current')}
                                            </div>

                                            {validationErrors.currentPassword && (
                                                <small className="text-danger mt-1 d-block">
                                                    {validationErrors.currentPassword}
                                                </small>
                                            )}
                                        </div>}

                                    <div className="upe-form-grid two">
                                        <div className="upe-field">
                                            <label>New Password</label>
                                            <div className="upe-password-wrap">
                                                <input
                                                    type={showPasswords.next ? 'text' : 'password'}
                                                    name="newPassword"
                                                    value={passwordData.newPassword}
                                                    onChange={handlePasswordChange}
                                                    placeholder="••••••••"
                                                    autoComplete="new-password"
                                                />
                                                {renderPasswordToggle('next')}
                                            </div>

                                            {validationErrors.newPassword && (
                                                <small className="text-danger mt-1 d-block">
                                                    {validationErrors.newPassword}
                                                </small>
                                            )}
                                        </div>

                                        <div className="upe-field">
                                            <label>Confirm New Password</label>
                                            <div className="upe-password-wrap">
                                                <input
                                                    type={showPasswords.confirm ? 'text' : 'password'}
                                                    name="confirmPassword"
                                                    value={passwordData.confirmPassword}
                                                    onChange={handlePasswordChange}
                                                    placeholder="••••••••"
                                                    autoComplete="new-password"
                                                />
                                                {renderPasswordToggle('confirm')}
                                            </div>

                                            {validationErrors.confirmPassword && (
                                                <small className="text-danger mt-1 d-block">
                                                    {validationErrors.confirmPassword}
                                                </small>
                                            )}
                                        </div>
                                    </div>


                                    {submitSection === 'security' && submitSuccess && (
                                        <div className="alert alert-success mt-3">
                                            {submitSuccess}
                                        </div>
                                    )}

                                    {submitSection === 'security' && submitError && (
                                        <div className="alert alert-danger mt-3">
                                            {submitError}
                                        </div>
                                    )}

                                    <div className="upe-actions end">
                                        <button
                                            type="submit"
                                            className="upe-primary-btn btn-click"
                                            disabled={submitLoading}
                                        >
                                            {submitLoading ? 'Updating...' : 'Update Password'}
                                        </button>
                                    </div>
                                </div>

                                <div className="upe-divider" />

                                <div className="upe-2fa-row">
                                    <div className="upe-2fa-left">
                                        <div className="upe-2fa-icon wf-icon-blue p-1">
                                            <GlobeAltIcon className="wf-icon-small" />
                                        </div>
                                        <div>
                                            <h5>Two-Factor Authentication</h5>
                                            <p>Add an extra layer of security to your account</p>
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        disabled
                                        className="upe-light-btn upe-light-btn--faded"
                                        title="Coming soon"
                                    >
                                        ENABLE
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>

            {showDeleteModal && canDeleteUser && (
                <div className="upe-modal-overlay">
                    <div className="upe-delete-modal">
                        <div className="upe-delete-icon">
                            <DeleteIcon className="wf-icon" />
                        </div>

                        <h3>Delete this User?</h3>

                        <p>
                            All associated data, including clusters and deployments
                            <br />
                            will be <span>permanently removed.</span>
                        </p>

                        <button
                            className="upe-delete-confirm-btn"
                            onClick={handleDeleteAccount}
                            disabled={submitLoading}
                        >
                            {submitLoading ? 'Deleting...' : 'Confirm Deletion'}
                        </button>

                        <button
                            className="upe-delete-cancel-btn"
                            onClick={() => setShowDeleteModal(false)}
                        >
                            I changed my mind
                        </button>
                    </div>
                </div>
            )}

            <div className="mobile-view-bottom"></div>
        </>
    );
};
